/**
 * Heatmap Chart Type Definition
 * Colored grid for confusion matrices and language × model performance tables
 */

import {
  titleControl,
  showValuesControl,
  valueDecimalsControl,
  xLabelControl,
  yLabelControl,
  labelRotationControl,
  truncateXAxisLabelsControl,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  axisLabelsCode,
  finishCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornAxisLabelsCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';
import { getSequentialPalette } from '../shared/palettes';

// Sample data: multilingual benchmark performance grid
const MODELS = ['Col A', 'Col B', 'Col C', 'Col D'];
const LANGUAGES = ['Row A', 'Row B', 'Row C', 'Row D', 'Row E', 'Row F'];
const BASE_SCORES = [
  [92.3, 91.5, 93.1, 88.4],
  [89.1, 88.7, 90.2, 85.3],
  [87.4, 86.9, 88.5, 83.1],
  [90.2, 89.8, 91.0, 87.2],
  [85.6, 84.2, 86.8, 82.4],
  [83.9, 82.7, 85.1, 80.8],
];

function generateSampleData() {
  const data = [];
  for (let yi = 0; yi < LANGUAGES.length; yi++) {
    for (let xi = 0; xi < MODELS.length; xi++) {
      data.push({ x: MODELS[xi], y: LANGUAGES[yi], value: BASE_SCORES[yi][xi] });
    }
  }
  return data;
}

const heatmap = {
  id: 'heatmap',
  name: 'Heatmap',
  category: 'heatmap',
  description: 'Colored grid for confusion matrices and performance tables',

  getSampleData: () => generateSampleData(),
  sampleData: generateSampleData(),

  defaultConfig: {
    colorPalette: 'blues',
    showValues: true,
    valueDecimals: 1,
    showCellBorder: false,
    xlabel: 'X',
    ylabel: 'Y',
    title: '',
    labelRotation: null,
    truncateXAxisLabels: false,
    outputFormat: 'matplotlib',
  },

  controls: [
    {
      key: 'colorPalette',
      label: 'Color Palette',
      type: 'select',
      options: [
        { label: 'Blues', value: 'blues' },
        { label: 'Greens', value: 'greens' },
        { label: 'Reds', value: 'reds' },
      ],
    },
    {
      key: 'showCellBorder',
      label: 'Show Cell Borders',
      type: 'toggle',
    },
    showValuesControl,
    valueDecimalsControl,
    labelRotationControl,
    truncateXAxisLabelsControl,
    xLabelControl,
    yLabelControl,
    titleControl,
  ],

  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded &&
      dataContext.mapping?.x && dataContext.mapping?.y && dataContext.mapping?.value;
    const decimals = config.valueDecimals ?? 1;
    const palette = getSequentialPalette(config, s);
    const linewidths = config.showCellBorder ? 0.5 : 0;

    const scoreRows = BASE_SCORES.map(row => `    [${row.join(', ')}]`).join(',\n');

    const dataSection = hasUserData
      ? `df = pd.read_csv('${dataContext.fileName}')
# Pivot from long to wide format
data_wide = df.pivot(index='${dataContext.mapping.y}', columns='${dataContext.mapping.x}', values='${dataContext.mapping.value}')
x_labels = data_wide.columns.tolist()
y_labels = data_wide.index.tolist()
data_matrix = data_wide.values`
      : `x_labels = ${JSON.stringify(MODELS)}
y_labels = ${JSON.stringify(LANGUAGES)}
data_matrix = np.array([
${scoreRows}
])`;

    const borderCode = config.showCellBorder
      ? `\n# Cell grid lines\nfor i in range(len(y_labels) + 1):\n    ax.axhline(i - 0.5, color='white', linewidth=0.5)\nfor j in range(len(x_labels) + 1):\n    ax.axvline(j - 0.5, color='white', linewidth=0.5)`
      : '';

    const valueLabelCode = config.showValues
      ? `\n# Value labels in cells\nfor i in range(len(y_labels)):\n    for j in range(len(x_labels)):\n        t = (data_matrix[i, j] - data_matrix.min()) / (data_matrix.max() - data_matrix.min() or 1)\n        text_color = 'white' if t > 0.55 else 'black'\n        ax.text(j, i, f'{data_matrix[i, j]:.${decimals}f}',\n                ha='center', va='center', fontsize=9, color=text_color)`
      : '\n# Value labels disabled';

    if (config.outputFormat === 'seaborn') {
      return `${seabornSetup(s)}

# ======== DATA ========
${dataSection}
# ======================
${seabornCreateFigure(8, 6, 300, s)}

# Build a colormap from the active chart palette
palette = ${JSON.stringify(palette)}
cmap = mcolors.LinearSegmentedColormap.from_list('coplot_heatmap', palette)

# Draw heatmap
sns.heatmap(
    data_matrix,
    xticklabels=x_labels,
    yticklabels=y_labels,
    annot=${config.showValues ? 'True' : 'False'},
    fmt='.${decimals}f',
    cmap=cmap,
    linewidths=${linewidths},
    linecolor='white',
    ax=ax,
    cbar=True,
    annot_kws={'fontsize': 9},
)
${seabornTitleCode(config, s)}
${seabornAxisLabelsCode(config, s)}
${seabornFinishCode('heatmap')}`;
    }

    // Matplotlib output (default)
    return `${matplotlibSetup(s)}

# ======== DATA ========
${dataSection}
# ======================
${createFigure(8, 6, 300, s)}

# Build a colormap from the active chart palette
palette = ${JSON.stringify(palette)}
cmap = mcolors.LinearSegmentedColormap.from_list('coplot_heatmap', palette)

# Draw heatmap using imshow
im = ax.imshow(data_matrix, cmap=cmap, aspect='auto',
               vmin=data_matrix.min(), vmax=data_matrix.max())

# Set tick labels
ax.set_xticks(np.arange(len(x_labels)))
ax.set_yticks(np.arange(len(y_labels)))
ax.set_xticklabels(x_labels, ha='center', fontsize=10)
ax.set_yticklabels(y_labels, fontsize=10)
${borderCode}
${valueLabelCode}

# Add colorbar
plt.colorbar(im, ax=ax, shrink=0.8)
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${finishCode('heatmap')}`;
  },
};

export default heatmap;
